/*
  Mobile menu background isolation.

  While the mobile menu drawer is open, everything behind it must be unreachable
  by keyboard and by screen readers. Today the drawer traps Tab, but the page
  behind it is still focusable: `.page` carries neither `inert` nor
  `aria-hidden`, so a screen reader can leave the drawer and the browser can
  still tab into the header, the collection grid and the footer.

  The drawer section is itself a CHILD of `.page` (see layout/theme.liquid), so
  `.page` cannot be made inert — that would disable the drawer too. Instead every
  direct child of `.page` that does not contain the drawer is isolated, using the
  same mechanism the theme already uses for its hidden mobile-menu panes
  (`inert` plus `aria-hidden`) — see assets/theme.js, paneIsBecomingVisible().

  The open/close state is read from `body[mobile-menu-open]`, which the theme's
  mobile-menu module sets to "true"/"false" in its own open()/close() functions
  (assets/theme.js, selectors `menuButton: [data-menu-button]`, classes
  `active` / `below-header`). Nothing else about the drawer is touched.

  Focus hand-back (why this file re-applies focus itself)
  ------------------------------------------------------
  The theme closes the drawer in ONE synchronous block whose order is:

      node.classList.remove('active')
      menuButton.setAttribute('aria-expanded', false)
      focusTrap.deactivate()                      <-- returns focus to [data-menu-button]
      document.body.setAttribute('mobile-menu-open', 'false')   <-- our only signal

  Focus is therefore handed back BEFORE this script can learn that the drawer
  closed, so the header subtree that holds the toggle is still `inert` at that
  moment and the theme's focus() call is discarded. The toggle would keep no
  focus and the hidden drawer would keep the old one. To keep the live theme's
  behaviour, we remove `inert` first and only then re-apply focus to the toggle.
  No timing guess is involved: the attribute mutation runs as a microtask
  immediately after the theme's close block, and the focus is re-applied in that
  same callback, straight after the attributes are restored.

  Every element we modify has its previous `inert` / `aria-hidden` value
  recorded, so closing the drawer restores exactly the earlier state and never
  leaves a stale `inert` behind that would block scrolling or clicking.
*/
(function () {
  if (window.__mobileMenuIsolation) { return; }
  window.__mobileMenuIsolation = true;

  var OPEN_ATTRIBUTE = 'mobile-menu-open';
  var DRAWER_SELECTOR = '.mobile-menu';
  var PAGE_SELECTOR = '.page';
  var TOGGLE_SELECTOR = '[data-menu-button]';

  var isolated = [];
  var wasOpen = false;

  var isOpen = function () {
    return document.body && document.body.getAttribute(OPEN_ATTRIBUTE) === 'true';
  };

  var backgroundChildren = function () {
    var page = document.querySelector(PAGE_SELECTOR);
    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!page || !drawer) { return []; }
    return [].filter.call(page.children, function (child) {
      return !child.contains(drawer);
    });
  };

  var restore = function () {
    isolated.forEach(function (entry) {
      var el = entry.el;
      if (entry.inert === null) { el.removeAttribute('inert'); } else { el.setAttribute('inert', entry.inert); }
      if (entry.ariaHidden === null) { el.removeAttribute('aria-hidden'); } else { el.setAttribute('aria-hidden', entry.ariaHidden); }
    });
    isolated = [];
  };

  // Runs AFTER `inert` has been removed, so the toggle is focusable again.
  var returnFocusToToggle = function () {
    var toggle = document.querySelector(TOGGLE_SELECTOR);
    if (!toggle) { return; }
    var active = document.activeElement;
    if (active === toggle) { return; }
    var drawer = document.querySelector(DRAWER_SELECTOR);
    // Only recover focus that this isolation stranded: it is still inside the
    // now-hidden drawer, or it was dropped when an ancestor became inert.
    // Never steal focus the user has deliberately moved somewhere else.
    var strandedInDrawer = !!(drawer && active && drawer.contains(active));
    var dropped = !active || active === document.body || active === document.documentElement;
    if (strandedInDrawer || dropped) { toggle.focus(); }
  };

  var hideFromAssistiveTech = function () {
    if (!isOpen()) { return; }
    isolated.forEach(function (entry) {
      var el = entry.el;
      // Never hide a subtree that still holds focus: the browser would reject
      // the attribute. `inert` has already moved focus out of the background.
      if (!el.isConnected || el.contains(document.activeElement)) { return; }
      el.setAttribute('aria-hidden', 'true');
    });
  };

  var isolate = function () {
    var targets = backgroundChildren();
    isolated = targets.map(function (el) {
      return { el: el, inert: el.getAttribute('inert'), ariaHidden: el.getAttribute('aria-hidden') };
    });
    targets.forEach(function (el) { el.setAttribute('inert', ''); });
    window.requestAnimationFrame(hideFromAssistiveTech);
    // The drawer's own focus trap activates ~50ms after opening, so repeat once
    // focus has settled inside the drawer.
    window.setTimeout(hideFromAssistiveTech, 150);
  };

  var apply = function () {
    var open = isOpen();
    if (open) {
      isolate();
    } else {
      // Un-isolate first, then hand focus back — never the other way round.
      restore();
      if (wasOpen) { returnFocusToToggle(); }
    }
    wasOpen = open;
  };

  var observe = function () {
    new MutationObserver(apply).observe(document.body, {
      attributes: true,
      attributeFilter: [OPEN_ATTRIBUTE]
    });
    apply();
  };

  if (document.body) { observe(); } else { document.addEventListener('DOMContentLoaded', observe); }
})();
